import { CSSProperties, useEffect } from "react";
import { clsx, makeKey } from "modules/Utils";
import useWebsiteBuilder, { getWebsiteBuilderPath, WebsiteBuilderContentType } from "Hooks/useWebsiteBuilder";
import { styx } from "modules/Utils";
import WebsiteBuilderPhonePreview from "./websiteBuilder/WebsiteBuilderPhonePreview";
import CardSymphony from "elements/CardSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { useHistory } from "react-router-dom";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import Icon from "elements/Icon";
import Loader from "elements/Loader";

interface WebsiteGalleryProps {
    className?: string;
    style?: CSSProperties;
    isCard?: boolean;
    zoom?: number;
    isFooter?: boolean;
    isSmallButton?: boolean;
    isButtonAbove?: boolean;
    isRedirect?: boolean;
}

const WebsiteGallery = ({
    className,
    style,
    isCard = true,
    zoom = 0.85,
    isFooter = false,
    isSmallButton = false,
    isButtonAbove = false,
    isRedirect = false,
}: WebsiteGalleryProps) => {
    const history = useHistory();
    const {
        currentBrand,
        isBrandAdmin,
    } = useBrand() as BrandExistsState;

    const {
        websitesGallery,
        getWebsitesGallery,
        cloneWebsite,
        isLoadingWebsitesGallery,
        setShowCreateNewWebsiteModalV2,
    } = useWebsiteBuilder();

    useEffect(() => {
        getWebsitesGallery({
            websiteIds: [
                36,
                342,
                196,
                213,
                114,
                202,
                183,
                178,
                35,
                197,
                188,
                157,
                159,
                153,
                115,
            ],
        });
    }, []);

    const websiteGalleryContent = websitesGallery.map((website, index) => {
        const {
            websiteContent,
            websiteStyle,
        } = website;

        // Get website title from content if available
        const websiteTitle = websiteContent.find(item =>
            item.type === WebsiteBuilderContentType.TEXT && item.textContent?.title)?.textContent?.title ||
            `Template ${index + 1}`;

        const brandName = website?.brand?.name;
        const websiteSlug = website?.slug;
        const brandSlug = website?.brand?.slug;

        const websiteBuilderPath = getWebsiteBuilderPath(currentBrand.slug, websiteSlug)

        const renderRemixButton = () => {
            if (!isBrandAdmin) {
                return null;
            }

            return (
                <ButtonSymphony
                    className="remixButton"
                    onClick={() => {
                        cloneWebsite({
                            websiteContent,
                            websiteStyle,
                        });
                        setShowCreateNewWebsiteModalV2(false)
                        if (isRedirect) {
                            history.push(websiteBuilderPath);
                        }
                    }}
                    size={isSmallButton ? 'small' : 'medium'}
                >
                    Remix Website
                </ButtonSymphony>
            )
        }

        return (
            <div
                key={makeKey('websiteGallery', brandName, index)}
                className="allWebsitesWebsitePreview"
                style={styx(
                    sty.allWebsitesWebsitePreview,
                    isSmallButton && {
                        gap: 8,
                    }
                )}
            >
                <div className="websiteTitle" style={sty.websiteTitle}>
                    <div>{brandName}</div>
                    <Icon
                        className="externalLinkIcon"
                        icon="externalLink"
                        onClick={() => {
                            window.open(`https://symphony.to/${brandSlug}/${websiteSlug}`, '_blank');
                        }}
                        size={20}
                    />
                </div>
                {isButtonAbove && renderRemixButton()}
                <WebsiteBuilderPhonePreview
                    key={makeKey('websiteTemplate', index)}
                    className="websiteTemplatePreviewPhonePreviewFromCreateNewWebsiteModalV2"
                    style={{
                        zoom,
                    }}
                    websiteContent={websiteContent}
                    websiteStyle={websiteStyle}
                    isSticky={false}
                    isFooter={isFooter}
                />
                {!isButtonAbove && renderRemixButton()}
            </div>
        )
    })

    if (isCard) {
        return (
            <CardSymphony
                className={clsx('websiteGalleryComponent', className)}
                style={styx(
                    sty.websiteGalleryComponent,
                    style
                )}
                title="Gallery"
                subtitle="It's time to get inspired.  Here are some of the best looking websites from our community."
                isNoPadding
                size="small"
            >
                {websiteGalleryContent}
            </CardSymphony>
        );
    }

    return (
        <div
            className={clsx('websiteGalleryComponent', className)}
            style={styx(
                sty.websiteGalleryComponent,
                style
            )}
        >
            {isLoadingWebsitesGallery && websiteGalleryContent.length === 0 &&
                <Loader
                    className="websiteGalleryLoader"
                    size={42}
                    title="Loading Website Gallery..."
                    isCentered
                />
            }
            {websiteGalleryContent}
        </div>
    )
};

const sty: Record<string, CSSProperties> = {
    websiteGalleryComponent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 24,
        flexWrap: 'wrap',
    },
    allWebsitesWebsitePreview: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
    },
    websiteTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontWeight: 500,
    },
    externalLinkIcon: {
        color: '#666',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
    },
};

export default WebsiteGallery;